import { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/axios";
import { colors, spacing, borderRadius, typography, styles, createButtonStyle, shadows } from "../styles/theme";

/** ===== Typy ===== */
type SourceType = "allegro" | "google_ads";

// Allegro
type AllegroClient = { id: string; nickname: string };
type CostsV1 = { cost: number; client_id: string; nickname: string };
type CostsV2 = { cpc?: number; display?: number; total?: number; conversions_value?: number; client_id: string; nickname: string };
type CostsAny = CostsV1 | CostsV2;

// Google Ads
type GoogleAdsClient = { customer_id: string; descriptive_name: string; mcc_email?: string };
type GoogleAdsCosts = {
  cost: number;
  cost_micros: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversions_value: number;
  currency_code: string;
  customer_id: string;
  descriptive_name: string;
  mcc_email?: string;
  cost_by_campaign_type: Record<string, number>;  // NEW: breakdown per campaign type
};

type MissingDaysResponse = string[] | { missing: string[] };

/** ===== Helpery daty bez „uciekania” do UTC ===== */
const fmt = (d: Date) => {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const previousMonthRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const start = new Date(m === 0 ? y - 1 : y, m === 0 ? 11 : m - 1, 1);
  const end = new Date(m === 0 ? y - 1 : y, (m === 0 ? 11 : m - 1) + 1, 0);
  return { from: fmt(start), to: fmt(end) };
};

export default function AllegroClients() {
  const [sourceType, setSourceType] = useState<SourceType>("allegro");

  const [clientSearch, setClientSearch] = useState("");
  const [allegroOptions, setAllegroOptions] = useState<AllegroClient[]>([]);
  const [googleOptions, setGoogleOptions] = useState<GoogleAdsClient[]>([]);
  const [selectedAllegro, setSelectedAllegro] = useState<AllegroClient | null>(null);
  const [selectedGoogle, setSelectedGoogle] = useState<GoogleAdsClient | null>(null);
  const [busySearch, setBusySearch] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | undefined>(undefined);

  const def = previousMonthRange();
  const [dateFrom, setDateFrom] = useState(def.from);
  const [dateTo, setDateTo] = useState(def.to);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allegroCosts, setAllegroCosts] = useState<{ cpc: number; gfx: number; total: number; conversions_value: number } | null>(null);
  const [googleCosts, setGoogleCosts] = useState<GoogleAdsCosts | null>(null);

  const [missingShown, setMissingShown] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);
  const [checkingMissing, setCheckingMissing] = useState(false);

  const canQuery = useMemo(() => {
    if (sourceType === "allegro") {
      return !!selectedAllegro && !!dateFrom && !!dateTo;
    } else {
      return !!selectedGoogle && !!dateFrom && !!dateTo;
    }
  }, [sourceType, selectedAllegro, selectedGoogle, dateFrom, dateTo]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setAllegroOptions([]);
        setGoogleOptions([]);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const handleSearch = (val: string) => {
    setClientSearch(val);
    setSelectedAllegro(null);
    setSelectedGoogle(null);
    setError(null);
    setAllegroOptions([]);
    setGoogleOptions([]);

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if ((val || "").trim().length < 2) return;

    debounceRef.current = window.setTimeout(async () => {
      try {
        setBusySearch(true);
        if (sourceType === "allegro") {
          const resp = await api.get<AllegroClient[]>("/allegro-clients", {
            params: { nickname: val.trim() },
          });
          setAllegroOptions(Array.isArray(resp.data) ? resp.data : []);
        } else {
          const resp = await api.get<GoogleAdsClient[]>("/google-ads/customers", {
            params: { search: val.trim() },
          });
          setGoogleOptions(Array.isArray(resp.data) ? resp.data : []);
        }
      } catch {
        setAllegroOptions([]);
        setGoogleOptions([]);
      } finally {
        setBusySearch(false);
      }
    }, 300);
  };

  const selectAllegroClient = (c: AllegroClient) => {
    setSelectedAllegro(c);
    setClientSearch(c.nickname);
    setAllegroOptions([]);
    setError(null);
    setAllegroCosts(null);
    setMissing([]);
    setMissingShown(false);
  };

  const selectGoogleClient = (c: GoogleAdsClient) => {
    setSelectedGoogle(c);
    setClientSearch(c.descriptive_name);
    setGoogleOptions([]);
    setError(null);
    setGoogleCosts(null);
    setMissing([]);
    setMissingShown(false);
  };

  const fetchCosts = async () => {
    if (!canQuery) return;
    setError(null);
    setLoading(true);
    setAllegroCosts(null); // Reset
    setGoogleCosts(null);

    try {
      if (sourceType === "allegro") {
        const resp = await api.get<CostsAny>("/allegro-costs", {
          params: {
            client_id: selectedAllegro!.id,
            date_from: dateFrom,
            date_to: dateTo,
          },
        });

        const d = resp.data as any;
        const total = d.total ?? d.cost ?? ((d.cpc || 0) + (d.display || 0));

        setAllegroCosts({
          cpc: Number(d.cpc ?? 0),
          gfx: Number(d.display ?? 0),
          total: Number(total ?? 0),
          conversions_value: Number(d.conversions_value ?? 0),
        });
      } else {
        const resp = await api.get<GoogleAdsCosts>("/google-ads/costs", {
          params: {
            customer_id: selectedGoogle!.customer_id,
            date_from: dateFrom,
            date_to: dateTo,
          },
        });
        setGoogleCosts(resp.data);
      }
    } catch (e: any) {
      const m = e?.response?.data?.detail || "Nie udało się pobrać kosztów.";
      setError(m);
    } finally {
      setLoading(false);
    }
  };

  // toggle + pobierz missing days
  const toggleMissing = async () => {
    if (!canQuery) return;
    const willShow = !missingShown;
    setMissingShown(willShow);
    if (!willShow) return;

    try {
      setCheckingMissing(true);
      if (sourceType === "allegro") {
        const resp = await api.get<MissingDaysResponse>("/missing-days", {
          params: { client_id: selectedAllegro!.id, date_from: dateFrom, date_to: dateTo },
        });
        const payload = resp.data as any;
        const arr: string[] = Array.isArray(payload) ? payload : payload?.missing ?? [];
        setMissing(arr);
      } else {
        const resp = await api.get<string[]>("/google-ads/missing-days", {
          params: { customer_id: selectedGoogle!.customer_id, date_from: dateFrom, date_to: dateTo },
        });
        setMissing(resp.data);
      }
    } catch {
      setMissing([]);
    } finally {
      setCheckingMissing(false);
    }
  };

  const handleSourceChange = (newSource: SourceType) => {
    setSourceType(newSource);
    setClientSearch("");
    setSelectedAllegro(null);
    setSelectedGoogle(null);
    setAllegroOptions([]);
    setGoogleOptions([]);
    setError(null);
    setAllegroCosts(null);
    setGoogleCosts(null);
    setMissing([]);
    setMissingShown(false);
  };

  const options = sourceType === "allegro" ? allegroOptions : googleOptions;

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: spacing.lg }}>
      <h2 style={{
        fontSize: typography.fontSize.xxxl,
        fontWeight: typography.fontWeight.bold,
        color: colors.textPrimary,
        marginBottom: spacing.lg
      }}>
        📊 Klienci Ads
      </h2>

      {error && (
        <div style={{
          padding: spacing.md,
          background: colors.errorLight,
          border: `1px solid ${colors.error}`,
          borderRadius: borderRadius.sm,
          color: colors.errorDark,
          marginBottom: spacing.md,
          fontSize: typography.fontSize.sm,
        }}>
          ❌ {error}
        </div>
      )}

      {/* Source selector */}
      <div style={{ marginBottom: spacing.md }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Źródło danych:
          </span>
          <select
            value={sourceType}
            onChange={(e) => handleSourceChange(e.target.value as SourceType)}
            style={styles.select}
          >
            <option value="allegro">Allegro Ads</option>
            <option value="google_ads">Google Ads</option>
          </select>
        </label>
      </div>

      {/* Client search */}
      <div ref={wrapperRef} style={{ position: "relative", marginBottom: spacing.lg }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            {sourceType === "allegro" ? "Klient Allegro:" : "Klient Google Ads:"}
          </span>
          <div style={{ position: "relative" }}>
            <input
              value={clientSearch}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Wpisz nazwę (min 2 znaki)"
              autoComplete="off"
              style={styles.input}
            />
            {busySearch && (
              <span style={{
                position: "absolute",
                right: spacing.md,
                top: "50%",
                transform: "translateY(-50%)",
                color: colors.textSecondary,
                fontSize: typography.fontSize.sm
              }}>
                ⏳
              </span>
            )}
          </div>
        </label>
        {!!options.length && (
          <ul style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            background: colors.background,
            border: `1px solid ${colors.border}`,
            listStyle: "none",
            margin: 0,
            padding: 0,
            maxHeight: 220,
            overflowY: "auto",
            boxShadow: shadows.lg,
            borderRadius: borderRadius.sm,
            zIndex: 10,
          }}>
            {sourceType === "allegro"
              ? (options as AllegroClient[]).map((c) => (
                  <li
                    key={c.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectAllegroClient(c)}
                    style={{ padding: spacing.md, cursor: "pointer", transition: "background 0.2s ease" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.primaryLight; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ fontWeight: typography.fontWeight.medium }}>{c.nickname}</div>
                    <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>{c.id}</div>
                  </li>
                ))
              : (options as GoogleAdsClient[]).map((c) => (
                  <li
                    key={c.customer_id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectGoogleClient(c)}
                    style={{ padding: spacing.md, cursor: "pointer", transition: "background 0.2s ease" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.primaryLight; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ fontWeight: typography.fontWeight.medium }}>{c.descriptive_name}</div>
                    <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                      {c.customer_id}
                      {c.mcc_email && <span style={{ marginLeft: spacing.xs }}>[MCC: {c.mcc_email}]</span>}
                    </div>
                  </li>
                ))}
          </ul>
        )}
      </div>

      <div style={{ marginBottom: spacing.lg }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.md, marginBottom: spacing.md }}>
          <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
              Data od:
            </span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={styles.input}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
              Data do:
            </span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={styles.input}
            />
          </label>
        </div>
        <div style={{ display: "flex", gap: spacing.md, flexWrap: "wrap" }}>
          <button
            onClick={fetchCosts}
            disabled={!canQuery || loading}
            style={createButtonStyle('primary', !canQuery || loading)}
          >
            {loading ? "⏳ Pobieram…" : "📊 Pokaż koszty"}
          </button>
          <button
            onClick={toggleMissing}
            disabled={!canQuery || checkingMissing}
            style={createButtonStyle('secondary', !canQuery || checkingMissing)}
          >
            {missingShown ? "👁️ Ukryj dni" : "🔍 Sprawdź dni"}
          </button>
        </div>
      </div>

      {/* Missing days */}
      {missingShown && (
        <div style={{
          padding: spacing.md,
          background: missing.length ? colors.warningLight : colors.successLight,
          border: `1px solid ${missing.length ? colors.warning : colors.success}`,
          borderRadius: borderRadius.sm,
          color: missing.length ? colors.warningDark : colors.successDark,
          marginBottom: spacing.md,
          fontSize: typography.fontSize.sm,
        }}>
          {checkingMissing
            ? "⏳ Sprawdzam…"
            : missing.length
            ? `⚠️ Brakuje danych/kosztów w dniach: ${missing.join(", ")}`
            : "✅ Brak brakujących dni."}
        </div>
      )}

      {/* Results - Allegro Ads */}
      {sourceType === "allegro" && allegroCosts && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10 }}>
          <Box title="PPC">{allegroCosts.cpc.toFixed(2)} zł</Box>
          <Box title="Graficzna">{allegroCosts.gfx.toFixed(2)} zł</Box>
          <Box title="Suma">{allegroCosts.total.toFixed(2)} zł</Box>
          <Box title="Wartość konwersji">{allegroCosts.conversions_value.toFixed(2)} zł</Box>
        </div>
      )}

      {/* Results - Google Ads */}
      {sourceType === "google_ads" && googleCosts && (
        <div style={{ marginTop: 10 }}>
          {/* Main metrics */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
            <Box title="Koszt">
              {googleCosts.cost.toFixed(2)} {googleCosts.currency_code}
            </Box>
            <Box title="Impressions">{googleCosts.impressions.toLocaleString()}</Box>
            <Box title="Clicks">{googleCosts.clicks.toLocaleString()}</Box>
            <Box title="Conversions">{googleCosts.conversions.toFixed(2)}</Box>
            <Box title="Conversions Value">
              {googleCosts.conversions_value.toFixed(2)} {googleCosts.currency_code}
            </Box>
          </div>

          {/* Breakdown per campaign type */}
          {Object.keys(googleCosts.cost_by_campaign_type || {}).length > 0 && (
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, marginBottom: 8, color: "#333" }}>
                Koszt per typ kampanii:
              </h3>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {Object.entries(googleCosts.cost_by_campaign_type).map(([campaignType, cost]) => (
                  <Box key={campaignType} title={campaignType}>
                    {cost.toFixed(2)} {googleCosts.currency_code}
                  </Box>
                ))}
              </div>
            </div>
          )}

          {/* Technical details */}
          <div style={{ fontSize: 12, color: "#666", marginTop: 10 }}>
            <div>Customer ID: {googleCosts.customer_id}</div>
            <div>Descriptive Name: {googleCosts.descriptive_name}</div>
            {googleCosts.mcc_email && <div>MCC Email: {googleCosts.mcc_email}</div>}
            <div>Cost (micros): {googleCosts.cost_micros.toLocaleString()}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const Box = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{
    border: `1px solid ${colors.border}`,
    background: colors.backgroundAlt,
    padding: spacing.lg,
    borderRadius: borderRadius.md,
    minWidth: 180,
    boxShadow: shadows.sm,
    transition: "all 0.2s ease",
  }}
  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = shadows.md; }}
  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = shadows.sm; }}
  >
    <div style={{
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
      fontWeight: typography.fontWeight.medium
    }}>
      {title}
    </div>
    <div style={{
      fontSize: typography.fontSize.xxl,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary
    }}>
      {children}
    </div>
  </div>
);
